import { useCallback, useEffect, useRef, useState } from 'react';
import { getLeadMessages, markLeadMessagesAsRead } from '../services/api.js';
import { messagingHelp } from '../utils/helpContent';
import HelpTooltip from './HelpTooltip';

// How often the open thread re-checks for inbound replies.
const POLL_INTERVAL_MS = 30000;

// How many bubbles the Conversation card shows before "Show full thread".
const CARD_BUBBLE_CAP = 3;

// The message thread. Staff write in the composer (the act row or the
// Conversation card), which bumps refreshTrigger after each send.
// `card` is the sidecart form: no header or empty state, the thread draws only
// when texts exist, capped at the last few bubbles.
const LeadMessaging = ({ authToken, leadId, lead, compact = false, card = false, onCountChange, onMessagesRead, refreshTrigger = 0 }) => {
  const [showAll, setShowAll] = useState(false);
  const [messages, setMessages] = useState([]);
  const hasCardThread = card && messages.length > 0;
  const [error, setError] = useState(null);
  const [fetchingMessages, setFetchingMessages] = useState(true);
  const messagesEndRef = useRef(null);
  const rootRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  // Check if messaging is enabled for this lead's location
  const messagingEnabled = lead?.location?.messaging_enabled ?? false;

  // Disable email functionality
  const emailEnabled = false;

  // Held in a ref so a parent passing an inline callback can't churn
  // fetchMessages' identity and restart the poll interval on every render.
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  // `silent` polls skip the spinner so the thread doesn't flicker every 30s.
  const fetchMessages = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setFetchingMessages(true);
      const response = await getLeadMessages(authToken, leadId);
      // Handle both paginated (response.results) and non-paginated (direct array) responses
      const messagesArray = response.results || response || [];
      setMessages(messagesArray);
      setError(null);

      // Update parent component with count
      if (onCountChangeRef.current) {
        onCountChangeRef.current(messagesArray.length);
      }
    } catch (err) {
      setError(err.response?.status === 401 ? 'Unauthorized: Please log in again.' : 'Failed to fetch messages');
    } finally {
      if (!silent) setFetchingMessages(false);
    }
  }, [authToken, leadId]);

  // Initial load + poll for inbound replies. Without this, a patient texting
  // back never appears until the user manually hits refresh.
  useEffect(() => {
    if (!leadId) return;

    fetchMessages();

    const interval = setInterval(() => {
      // Don't poll a backgrounded tab — it just burns requests.
      if (document.visibilityState === 'visible') {
        fetchMessages({ silent: true });
      }
    }, POLL_INTERVAL_MS);

    // Catch up immediately when the user comes back to the tab.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchMessages({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [leadId, fetchMessages]);

  // The act row bumps this after a send. Sending is an explicit intent to see
  // your own message land, so the thread jumps to the bottom.
  useEffect(() => {
    if (refreshTrigger > 0) {
      isAtBottomRef.current = true;
      fetchMessages({ silent: true });
    }
  }, [refreshTrigger, fetchMessages]);

  // Only treat the thread as read once it has actually been on screen. The
  // section is expanded by default but sits below the fold, so mounting alone
  // is not "viewed" — marking on mount silently cleared unread state.
  // Navigating to another lead in the sidecart reuses this component, so the
  // visibility gate has to reset or the next lead is marked read on arrival.
  useEffect(() => {
    setHasBeenVisible(false);
    setShowAll(false);
  }, [leadId]);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || hasBeenVisible) return;

    if (typeof IntersectionObserver === 'undefined') {
      setHasBeenVisible(true); // no observer support: fall back to previous behaviour
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasBeenVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(node);
    return () => observer.disconnect();
    // The card draws no thread until texts exist, so the node can arrive late.
  }, [hasBeenVisible, hasCardThread]);

  // Mark messages as read when component is viewed.
  // NOTE: do NOT gate this on the `authToken` prop. That prop is threaded from
  // context and can be empty in some session states, which silently skipped the
  // mark-read call while GETs still worked (they auth via the axios interceptor,
  // not this prop). Auth is handled by the interceptor, same as markLeadAsRead.
  useEffect(() => {
    if (hasBeenVisible && leadId && messages.length > 0) {
      const hasUnreadMessages = messages.some(msg => msg.direction === 'inbound' && !msg.read_at);
      if (hasUnreadMessages) {
        markLeadMessagesAsRead(leadId).then(() => {
          if (onMessagesRead) onMessagesRead();
        }).catch(err => {
          console.error('Failed to mark messages as read:', err);
        });
      }
    }
  }, [leadId, messages, hasBeenVisible]);

  // Auto-scroll to the newest message — but only when the user is already at
  // the bottom. This used to fire unconditionally on every `messages` change,
  // which includes the silent 30s poll: scroll up to read history and the next
  // poll yanked you back down. Tracked on scroll rather than measured after
  // render, because by then scrollHeight already includes the new message.
  useEffect(() => {
    if (!isAtBottomRef.current) return;
    const messagesContainer = messagesEndRef.current?.closest('.messages-container');
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }, [messages]);

  // A different lead is a different conversation — start it at the bottom.
  useEffect(() => {
    isAtBottomRef.current = true;
  }, [leadId]);

  const handleThreadScroll = (e) => {
    const el = e.currentTarget;
    isAtBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  // A bubble labels its channel only when the lead has more than one.
  const channelCount = (lead?.phone ? 1 : 0) + (lead?.email && emailEnabled ? 1 : 0);

  // Conversation state: when staff wrote last, say so. A waiting reply shows
  // on the lead's reason line only, so an inbound last message shows nothing.
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const conversationState = (() => {
    if (!lastMessage || lastMessage.direction === 'inbound') return null;
    const ageMs = Date.now() - new Date(lastMessage.sent_at).getTime();
    const hours = Math.floor(ageMs / 3600000);
    const days = Math.floor(hours / 24);
    const ago = days > 0 ? `${days}d ago` : hours > 0 ? `${hours}h ago` : 'just now';
    return `You messaged ${ago} — no reply yet`;
  })();

  // Bubbles carry the time only; the date lives in a separator between days, so
  // a long thread stops repeating the same date on every single message.
  const sameDay = (a, b) => a.toDateString() === b.toDateString();

  const dayLabel = (iso) => {
    const date = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (sameDay(date, today)) return 'Today';
    if (sameDay(date, yesterday)) return 'Yesterday';
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const timeLabel = (iso) =>
    new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  if (!lead) {
    return <div className="text-center py-4 text-gray-500">Loading lead details...</div>;
  }

  // Don't render messaging if not enabled for this location
  if (!messagingEnabled) {
    // The card's composer already says why texting is off.
    if (card) return null;
    return (
      <div className="text-center py-8 text-gray-500">
        <svg className="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.137 0-4.146.832-5.636 2.364M12 21l3.464-2l1.536-2H12v4z" />
        </svg>
        <p className="text-lg font-medium">Messaging not available</p>
        <p className="text-sm">Please contact your marketing agency to enable</p>
      </div>
    );
  }

  const hiddenCount = messages.length - CARD_BUBBLE_CAP;
  const shownMessages = card && !showAll ? messages.slice(-CARD_BUBBLE_CAP) : messages;

  if (card && (fetchingMessages || messages.length === 0)) {
    return error ? <p className="text-xs font-medium text-red-700">{error}</p> : null;
  }

  return (
    <div
      ref={rootRef}
      data-testid={card ? "conversation-thread" : undefined}
      className={`${card ? "" : compact ? "max-h-[32rem]" : "max-h-[40rem]"} flex flex-col bg-gray-50`}
    >
      {/* The thread also polls every 30s and on tab focus. */}
      {!card && (
      <div className="flex items-center justify-end gap-1 bg-white border-b border-gray-200 px-2 py-1">
        <HelpTooltip
          title={messagingHelp.refreshMessages.title}
          content={messagingHelp.refreshMessages.content}
          size="sm"
          position="top-left"
        />
        <button
          onClick={() => fetchMessages()}
          disabled={fetchingMessages}
          className="p-2 rounded-lg text-gray-400 hover:text-teal-600 transition-all disabled:opacity-50"
          title="Refresh messages"
        >
          <svg
            className={`h-4 w-4 ${fetchingMessages ? "animate-spin" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>
      )}

      {card && hiddenCount > 0 && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="px-3 py-1.5 text-left text-xs font-medium text-primary hover:underline"
        >
          Show full thread
        </button>
      )}

      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200">
          <p className="text-red-700 text-sm font-medium">{error}</p>
        </div>
      )}

      {/* Conversation state - who owes whom a reply */}
      {conversationState && (
        <div className="px-4 py-2 text-xs font-medium border-b bg-gray-50 text-gray-600 border-gray-200">
          {conversationState}
        </div>
      )}

      {/* Messages List */}
      <div
        className={`flex-1 min-h-0 overflow-y-auto messages-container bg-background ${
          card ? "max-h-72 p-3 space-y-2" : "p-6 space-y-4"
        }`}
        onScroll={handleThreadScroll}
      >
        {/* The total lives in the section header badge — no need to repeat it
            inside the scroll area. */}
        {fetchingMessages ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-3"></div>
            <p className="text-gray-500 text-sm">Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <div className="bg-white rounded-full p-4 mb-4 shadow-sm">
              <svg
                className="h-8 w-8 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <p className="text-sm font-medium">No messages yet</p>
            <p className="text-xs text-gray-400 mt-1">
              Start a conversation above
            </p>
          </div>
        ) : (
          <>
            {shownMessages.map((message, index) => {
              const previous = index > 0 ? shownMessages[index - 1] : null;
              const startsNewDay =
                !previous ||
                !sameDay(new Date(previous.sent_at), new Date(message.sent_at));
              const isOutbound = message.direction === "outbound";
              const failed = isOutbound && message.status !== "sent";

              return (
                <div key={message.id}>
                  {startsNewDay && (
                    <div className="flex items-center gap-3 py-2">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-[11px] font-medium text-gray-400 whitespace-nowrap">
                        {dayLabel(message.sent_at)}
                      </span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                  )}

                  <div
                    className={`flex animate-fadeIn ${isOutbound ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[75%] px-4 py-2.5 rounded-2xl shadow-sm transition-all hover:shadow-md ${
                        isOutbound
                          ? "bg-gradient-to-br from-primary to-primary-dark text-white rounded-br-sm"
                          : "bg-white text-gray-900 rounded-bl-sm border border-gray-200"
                      }`}
                    >
                      {/* The channel is only worth labelling when more than one
                          exists — otherwise it's "SMS" stamped on every bubble. */}
                      {channelCount > 1 && (
                        <span
                          className={`block text-[10px] font-semibold uppercase tracking-wide mb-1 ${
                            isOutbound ? "text-primary-lighter" : "text-gray-500"
                          }`}
                        >
                          {message.message_type}
                        </span>
                      )}

                      <p className="ph-mask text-sm leading-relaxed">{message.content}</p>

                      {/* Delivery is the norm, so it's a glyph next to the time.
                          Failure is the exception and gets spelled out. */}
                      <div
                        className={`flex items-center justify-end gap-1 mt-1 text-[10px] ${
                          isOutbound ? "text-primary-lighter/80" : "text-gray-400"
                        }`}
                      >
                        <span>{timeLabel(message.sent_at)}</span>
                        {isOutbound && !failed && (
                          <svg
                            className="h-3 w-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}
                            aria-label="Sent"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>

                      {failed && (
                        <div className="flex items-center text-[10px] mt-2 pt-2 border-t border-red-300/20 text-red-200">
                          <svg
                            className="h-3 w-3 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                          Failed to send
                          <HelpTooltip
                            title={messagingHelp.messageStatuses.title}
                            content={messagingHelp.messageStatuses.content}
                            className="ml-1"
                            size="sm"
                          />
                        </div>
                      )}

                      {/* Error Message */}
                      {message.error_message && (
                        <p className="text-xs text-red-200 mt-2 pt-2 border-t border-red-300/20">
                          {message.error_message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>
    </div>
  );
};

export default LeadMessaging;